import { Injectable, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { ROUTER_CANCEL } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { merge, Subject } from 'rxjs';
import { first, map, share, shareReplay, skip, startWith, switchMap, takeUntil, withLatestFrom } from 'rxjs/operators';
import { DataService } from '../form-core/data/data.service';
import { BannerModel, ComponentModel, FieldsetModel, InfoModel, PickID, ServiceStepID, ServiceStepModel, StepID, StepModel } from '../form-json.types';
import { activateFail, activateSuccess } from '../guards/activate/activate.guard';
import { deactivateFail } from '../guards/deactivate/deactivate.guard';
import { State } from '../store';
import { selectStepID } from '../store/router-reducers';

@Injectable()
export class ScrollService implements OnDestroy {

  private destroy$: Subject<any> = new Subject()
  scrollToEdit: Subject<string> = new Subject()

  private activateFailHandler(redirectTo: PickID<StepModel | ServiceStepModel>) {
    const step = this.data.getModel<StepModel>(redirectTo)
    const fieldsets = step.relationships.fieldsets.data.map(fieldset => this.data.getModel<FieldsetModel>(fieldset))
    const stepFormGroup = this.data.stepsFormGroup[redirectTo.id]
    const controls = stepFormGroup.controls
    let invalid: string | undefined
    for (let controlName in controls) {
      const control = controls[controlName]
      if (control.invalid) {
        control.untouched && control.markAsTouched()
        control.pristine && control.markAsDirty()
      }
    }
    for (let i = 0; i < fieldsets.length; i++) {
      let toBreak: boolean = false
      const components = fieldsets[i].relationships.components?.data?.map(component => this.data.getModel<ComponentModel | BannerModel | InfoModel>(component))
      if (components) {
        for (let j = 0; j < (components.length || 0); j++) {
          const component = components[j]
          if (component.type !== 'info-component' && component.type !== 'intro-component') {
            const path = component.attributes.path
            const control = controls[path]
            if (control.invalid) {
              invalid = path
              toBreak = true
              break
            }
          }
        }
      }
      if (toBreak) break
    }
    return invalid
  }

  notice$ = this.actions$.pipe(
    ofType(deactivateFail),
    switchMap(() => this.actions$.pipe(
      ofType(ROUTER_CANCEL),
      withLatestFrom(this.store.select(selectStepID), (action, id) => {
        const step = this.data.getModel<StepModel>(this.data.getSteps().find(x => x.id === id) as StepID | ServiceStepID)
        const fieldsets = step.relationships.fieldsets.data.map(value => this.data.getModel<FieldsetModel>(value))
        const controls = fieldsets.reduce((pre, cur) => {
          return [...pre, ...cur.relationships.components?.data?.map(component => {
            if (component.type === 'info-component' || component.type === 'intro-component') return {}
            const path = this.data.getModel<ComponentModel>(component).attributes.path
            const control = this.data.getControl(path)
            const obj: { [key: string]: FormControl } = {}
            obj[path] = control
            return obj
          }).filter(obj => Object.keys(obj).length > 0) || []]
        }, [] as { [key: string]: FormControl }[])
        let path: string | undefined = undefined
        for (let i = 0; i < controls.length; i++) {
          path = Object.keys(controls[i])[0]
          if (controls[i][path].invalid) break
        }
        return path
      }),
      first()
    )),
    takeUntil(this.destroy$),
    share()
  )

  /* onInit$ = merge(
    merge(
      this.actions$.pipe(ofType(activateFail)),
      this.scrollToEdit
    ).pipe(
      switchMap(x => typeof x === 'string' ? this.actions$.pipe(
        ofType(activateSuccess),
        skip(1),
        startWith(x)
      ) : this.actions$.pipe(
        ofType(activateSuccess),
        skip(1)
      )),
      startWith(activateSuccess())
    ),
    this.actions$.pipe(
      ofType(activateFail),
      map(({ redirectTo }) => this.activateFailHandler(redirectTo))
    )
  ).pipe(
    takeUntil(this.destroy$),
    shareReplay(1)
  ) */

  onInit$ = merge(
    this.scrollToEdit,
    this.actions$.pipe(
      ofType(activateFail),
      map(({ redirectTo }) => this.activateFailHandler(redirectTo))
    ),
    merge(
      this.scrollToEdit,
      this.actions$.pipe(ofType(activateFail))
    ).pipe(
      switchMap(() => this.actions$.pipe(
        ofType(activateSuccess),
        skip(1)
      ))
    )
  ).pipe(
    startWith(activateSuccess({step: ''})),
    takeUntil(this.destroy$),
    shareReplay(1)
  )

  constructor(private actions$: Actions, private store: Store<State>, private data: DataService) { }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
    this.scrollToEdit.next()
    this.destroy$.complete()
  }
}
